/**
 * Budget Context API Client
 * Backs the "Hours to Complete" column: how much budget a resource has left on a
 * project, and the estimate they last submitted for it.
 */

import { apiClient } from '@/services/apiClient';

export interface BudgetContext {
  budgetHours: number;
  loggedHours: number;
  remainingHours: number;
  isAllocated: boolean;
  /** Estimate from a timesheet submitted within the carry-forward window. null = none. */
  recentSavedHtc: number | null;
}

/** How the resource's hours on the project are budgeted. */
export type AllocationMode = 'assign' | 'scheduler' | 'none';

export interface BudgetContextDto extends BudgetContext {
  projectId: number;
  employeeId: number;
  allocationMode: AllocationMode;
}

/** Rows on the same project share a response for this long. */
const BUDGET_CONTEXT_CACHE_TTL_MS = 60_000;

export async function getProjectBudgetContext(
  projectId: string | number,
  employeeId: number,
): Promise<BudgetContextDto> {
  const params = new URLSearchParams({ employeeId: String(employeeId) });

  return apiClient.get<BudgetContextDto>(
    `/projects/${projectId}/budget-context?${params.toString()}`,
    { cacheTtlMs: BUDGET_CONTEXT_CACHE_TTL_MS },
  );
}

/**
 * Resolves the initial HTC value to pre-populate on first load.
 *
 * Priority order:
 *   1. recentSavedHtc !== null → carry it forward
 *   2. isAllocated && remainingHours > 0 → use remainingHours
 *   3. Everything else (no allocation, negative/zero remaining) → 0
 *
 * Returns null only when there is no budget context to resolve from.
 */
export function resolveInitialHtc(ctx: BudgetContext | null): number | null {
  if (ctx === null) return null;
  if (ctx.recentSavedHtc !== null) return ctx.recentSavedHtc;
  if (ctx.isAllocated && ctx.remainingHours > 0) return ctx.remainingHours;
  return 0;
}
